import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useEffect } from 'react';

import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import type { AssetApiRecord, AssetsListResponse } from '@/pages/dam/api/assets.types';
import { relativeUrlResolver } from '@/services/utils';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

const LIBRARY_QUERY_KEY = ['assets', 'library'];

type LibraryData = InfiniteData<AssetsListResponse, unknown>;

interface AssetChangedEvent {
    typeOfChange?: 'changed' | 'deleted';
    primaryKeys?: string[];
}

const fetchAsset = async (primaryKey: string): Promise<AssetApiRecord> =>
    (await axios.get(relativeUrlResolver(`/api/v2/assets/${encodeURIComponent(primaryKey)}`))).data;

/**
 * Keeps the loaded library rows current like the item list does with `itemChanged`: on
 * `assetChanged` the visible affected assets are reloaded one by one and patched in place,
 * deleted ones (or ones that can no longer be loaded) are removed. Assets that are not
 * loaded are ignored; the next search picks them up.
 */
function useDamAssetsRealtimeUpdates() {
    const queryClient = useQueryClient();

    const updateLibrary = useCallback((change: (asset: AssetApiRecord) => AssetApiRecord | null) => {
        queryClient.getQueriesData<LibraryData>({ queryKey: LIBRARY_QUERY_KEY }).forEach(([key, data]) => {
            if (!data?.pages) {
                return;
            }
            let removed = 0;
            const pages = data.pages.map((page) => {
                const rows = page.data.flatMap((asset) => {
                    const next = change(asset);
                    if (next === null) {
                        removed += 1;
                        return [];
                    }
                    return [next];
                });
                return rows.length === page.data.length && rows.every((row, index) => row === page.data[index])
                    ? page
                    : { ...page, data: rows };
            });
            if (pages.some((page, index) => page !== data.pages[index])) {
                const first = pages[0];
                queryClient.setQueryData<LibraryData>(key, {
                    ...data,
                    pages: removed > 0 && first ? [{ ...first, found: Math.max(0, first.found - removed) }, ...pages.slice(1)] : pages,
                });
            }
        });
    }, [queryClient]);

    const loadedPrimaryKeys = useCallback(() => {
        const keys = new Set<string>();
        queryClient.getQueriesData<LibraryData>({ queryKey: LIBRARY_QUERY_KEY }).forEach(([, data]) => {
            data?.pages?.forEach((page) => page.data.forEach((asset) => keys.add(String(asset.primaryKey__))));
        });
        return keys;
    }, [queryClient]);

    const handleAssetChanged = useCallback(async (event: AssetChangedEvent) => {
        const loaded = loadedPrimaryKeys();
        const affected = (event?.primaryKeys ?? []).filter((primaryKey) => loaded.has(primaryKey));
        if (affected.length === 0) {
            return;
        }
        if (event.typeOfChange === 'deleted') {
            const deleted = new Set(affected);
            updateLibrary((asset) => (deleted.has(String(asset.primaryKey__)) ? null : asset));
            return;
        }
        const results = await Promise.allSettled(affected.map(fetchAsset));
        const fresh = new Map<string, AssetApiRecord | null>();
        results.forEach((result, index) => {
            fresh.set(affected[index], result.status === 'fulfilled' ? result.value : null);
        });
        updateLibrary((asset) => {
            const primaryKey = String(asset.primaryKey__);
            if (!fresh.has(primaryKey)) {
                return asset;
            }
            const loadedAsset = fresh.get(primaryKey);
            return loadedAsset ? { ...asset, ...loadedAsset } : null;
        });
    }, [loadedPrimaryKeys, updateLibrary]);

    const { subscribe, unsubscribe } = useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.ASSET_CHANGED_EVENT,
        eventHandler: handleAssetChanged,
    });

    useEffect(() => {
        void subscribe();
        return () => {
            void unsubscribe();
        };
    }, [subscribe, unsubscribe]);
}

export { useDamAssetsRealtimeUpdates };
